import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothWiseBeneficiary = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    const lang = language || 'en';
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");


    const location = useLocation();

    // Translations
    const t = {
        en: {
            pageTitle: "Booth Wise Beneficiary",
            voters: "Voters",
            beneficiaries: "Beneficiaries",
            loadingData: "Loading Data...",
            noData: "No data found.",
            searchPlaceholder: "Search Booth No...",
            boothPrefix: "Booth "
        },
        hi: {
            pageTitle: "बूथ अनुसार लाभार्थी",
            voters: "मतदाता",
            beneficiaries: "लाभार्थी",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई डेटा नहीं मिला।",
            searchPlaceholder: "बूथ नं. खोजें...",
            boothPrefix: "बूथ "
        },
        gu: {
            pageTitle: "બૂથ પ્રમાણે લાભાર્થી",
            voters: "મતદારો",
            beneficiaries: "લાભાર્થીઓ",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ ડેટા મળ્યો નથી.",
            searchPlaceholder: "બૂથ નં. શોધો...",
            boothPrefix: "બૂથ "
        }
    };

    const txt = t[lang] || t.gu;

    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_beneficiary_dash`, {
                app_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                setData(response.data.data.result);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error fetching booth wise beneficiary:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter Data based on Search Term (Booth No)
    const filteredData = data.filter(item => {
        const boothNoStr = (item.booth_no || item.part_no || "").toString();
        return boothNoStr.includes(searchTerm);
    });

    return (
        <div className="booth-beneficiary-page">
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">
                
                {/* Search Bar */}
                <div className="search-container mb-4 slide-up">
                    <div className="search-wrapper">
                      
                        <input
                            className="search-input"
                            type="text"
                            placeholder={txt.searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                        <p className="mt-3 text-muted fw-bold">{txt.loadingData}</p>
                    </div>
                ) : filteredData.length > 0 ? (
                    <div className="grid-container slide-up" style={{ animationDelay: '0.1s' }}>
                        {filteredData.map((item, index) => {
                            // Percentage Calculation
                            const totalVoter = item.total_voter || 0;
                            const totalBen = item.total_beneficiaries || item.total_beneficiary || 0;
                            const percentage = totalVoter > 0 ? Math.round((totalBen / totalVoter) * 100) : 0;
                            const boothName = item.booth_no || item.part_no || "N/A";

                            return (
                                <div className="data-card" key={index} onClick={()=>navigate('/sel_scheme_beneficiary_by_booth',{ state : {booth_no : boothName}})}>
                                    
                                    {/* Card Header: Booth No & Percentage */}
                                    <div className="card-header">
                                        <div className="booth-title">
                                            {txt.boothPrefix} {boothName}
                                        </div>
                                        <div className="pct-badge">
                                            {percentage}%
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="progress-bar-wrapper">
                                        <div className="progress-bar-bg">
                                            <div 
                                                className="progress-bar-fill" 
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    {/* Card Body: Stats in One Row */}
                                    <div className="card-body-custom">
                                        <div className="stats-row-one-line">
                                            {/* Voters */}
                                            <div className="stat-item">
                                                <div className="icon-wrap icon-blue">
                                                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>
                                                </div>
                                                <div className="stat-details">
                                                    <span className="stat-num text-blue">{totalVoter.toLocaleString('en-IN')}</span>
                                                    <span className="stat-lbl">{txt.voters}</span>
                                                </div>
                                            </div>

                                            {/* Beneficiaries */}
                                            <div className="stat-item">
                                                <div className="icon-wrap icon-green">
                                                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                                </div>
                                                <div className="stat-details">
                                                    <span className="stat-num text-green">{totalBen.toLocaleString('en-IN')}</span>
                                                    <span className="stat-lbl">{txt.beneficiaries}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center p-5 bg-white rounded-4 shadow-sm border mt-3">
                        <div style={{ fontSize: '40px', opacity: 0.5, marginBottom: '10px' }}>📄</div>
                        <h5 className="text-muted m-0">{txt.noData}</h5>
                    </div>
                )}
            </div>

            <style jsx>{`
                .booth-beneficiary-page { 
                    width: 100%; 
                    min-height: 100vh; 
                    background: #eef2f6; 
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; 
                    padding-bottom: 40px; 
                }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                /* Search Bar */
                .search-container { display: flex; justify-content: flex-end; }
                .search-wrapper { position: relative; width: 100%; max-width: 400px; display: flex; align-items: center; background: white; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0 16px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .search-icon { width: 20px; height: 20px; color: #94a3b8; }
                .search-input { width: 100%; border: none; padding: 12px 10px; font-size: 14px; color: #1e293b; outline: none; background: transparent; }

                /* Grid Layout */
                .grid-container {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 20px;
                }

                /* Mobile specific grid (2 columns) matching the image */
                @media (max-width: 640px) {
                    .grid-container {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 12px;
                    }
                }

                /* Data Card Styling */
                .data-card {
                    background: white;
                    border-radius: 16px;
                    padding: 16px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s, box-shadow 0.2s;
                    cursor: pointer;
                }
                .data-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 20px rgba(0,0,0,0.08);
                }

                /* Card Header */
                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 14px;
                    margin-bottom: 14px;
                }

                .booth-title {
                    font-size: 18px;
                    font-weight: 800;
                    color: #0f172a;
                }

                .pct-badge {
                    background: #dcfce7;
                    color: #16a34a;
                    font-size: 12px;
                    font-weight: 800;
                    padding: 4px 10px;
                    border-radius: 20px;
                    border: 1px solid #bbf7d0;
                }

                /* Progress Bar */
                .progress-bar-wrapper {
                    margin-bottom: 14px;
                }
                .progress-bar-bg {
                    background: #f1f5f9;
                    border-radius: 20px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-bar-fill {
                    background: linear-gradient(90deg, #22c55e, #16a34a);
                    border-radius: 20px;
                    height: 100%;
                    transition: width 0.8s ease;
                }

                /* Card Body Stats - One Row */
                .card-body-custom {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .stats-row-one-line {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                    gap: 10px;
                }

                .stat-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;
                }

                .icon-wrap {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .icon-blue { background: #eff6ff; color: #3b82f6; }
                .icon-green { background: #f0fdf4; color: #22c55e; }

                .stat-details {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    flex: 1;
                }

                .stat-num {
                    font-size: 16px;
                    font-weight: 800;
                    line-height: 1.2;
                    margin-bottom: 2px;
                }

                .text-blue { color: #2563eb; }
                .text-green { color: #16a34a; }

                .stat-lbl {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                }

                /* Animations */
                .slide-up { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                /* Small Mobile Adjustments */
                @media (max-width: 380px) {
                    .data-card { padding: 12px; }
                    .booth-title { font-size: 16px; }
                    .icon-wrap { width: 28px; height: 28px; }
                    .icon-wrap svg { width: 14px; height: 14px; }
                    .stat-num { font-size: 14px; }
                    .stat-lbl { font-size: 10px; }
                    .stat-item { gap: 8px; }
                    .progress-bar-bg { height: 4px; }
                }
            `}</style>
        </div>
    );
};

export default BoothWiseBeneficiary;